import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboard, faPlay, faShareNodes, faDownload, faUpload, faChevronDown, faDatabase, faCopy, faCheck } from "@fortawesome/free-solid-svg-icons";
import { db } from "../firebase";
import { collection, addDoc, getDocs, query, where, deleteDoc, doc, serverTimestamp, Timestamp } from "firebase/firestore";
import Footer from "../components/Footer";
import { updateCounter } from "../updateCounter";
import { useEffect } from "react";
import { useRef } from "react";

export default function ClipboardCard() {
  // use state
  const [activeTab, setActiveTab] = useState("share");
  const [text, setText] = useState("");
  const [expiry, setExpiry] = useState("1 Hour");
  const [oneTime, setOneTime] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(null);
  const [lastGeneratedText, setLastGeneratedText] = useState("");
  const [generateError, setGenerateError] = useState("");
  const [copied, setCopied] = useState(false);
  const [retrievedCopied, setRetrievedCopied] = useState(false);
  const [retrieveCode, setRetrieveCode] = useState(["", "", "", "", "", ""]);
  const [retrievedText, setRetrievedText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const resultRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  // GENERATE CODE + SAVE TO FIREBASE
  const generateCode = async (autoCopy = false) => {
    if (!text.trim()) return;

    // Same content hoy to navo code generate na thay
    if (text === lastGeneratedText) {

      setGenerateError("Code already generated for this text.");

      setTimeout(() => {
        setGenerateError("");
      }, 3000);

      return;
    }

    const code = Math.floor(100000 + Math.random() * 900000);
    setGeneratedCode(code);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }, 200);
    setLastGeneratedText(text);

    let duration = 60 * 60 * 1000;
    if (expiry === "1 Minutes") duration = 60 * 1000;
    if (expiry === "30 Minutes") duration = 30 * 60 * 1000;
    if (expiry === "24 Hours") duration = 24 * 60 * 60 * 1000;

    try {
      await addDoc(collection(db, "notes"), {
        text: text,
        code: code,
        createdAt: serverTimestamp(),
        expiryAt: Timestamp.fromDate(
          new Date(Date.now() + duration)
        ),
        oneTimeView: oneTime
      });
      updateCounter("share_click");

      // auto generate and auto copy if (autoCopy) {
      //   await navigator.clipboard.writeText(code);
      //   setQuickCopied(true);

      //   setTimeout(() => {
      //     setQuickCopied(false);
      //   }, 2000);
      // }

    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  // COPY CODE 
  const copyCode = async () => {
    if (!generatedCode) return;

    await navigator.clipboard.writeText(generatedCode);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  // COPY RETRIEVED TEXT
  const handleCopy = async () => {
    if (!retrievedText) {
      setErrorMessage("No text available to copy.");
      return;
    }

    await navigator.clipboard.writeText(retrievedText);
    setRetrievedCopied(true);

    setTimeout(() => {
      setRetrievedCopied(false);
    }, 2000);
  };

  // HANDLE 6-DIGIT PASTE
  const handlePaste = (e) => {
    e.preventDefault();

    const pastedData = e.clipboardData.getData("text").trim();

    // Only allow exactly 6 digits
    if (!/^\d{6}$/.test(pastedData)) return;

    const newCode = pastedData.split("");
    setRetrieveCode(newCode);

    // focus last box
    document.getElementById("code-5")?.focus();
  };

  const deleteExpiredNotes = async () => {
    try {
      const now = Timestamp.now();

      const q = query(
        collection(db, "notes"),
        where("expiryAt", "<=", now)
      );

      const snapshot = await getDocs(q);

      for (const document of snapshot.docs) {
        await deleteDoc(doc(db, "notes", document.id));
      }

      console.log("Expired notes removed:", snapshot.docs.length);
    } catch (error) {
      console.log("Failed to clean expired notes:", error);
    }
  };

  // RETRIEVE FROM FIREBASE 
  const handleRetrieve = async () => {
    console.log("Retrieve button clicked");

    setIsLoading(true);

    const code = Number(retrieveCode.join(""));

    setErrorMessage("");
    setRetrievedText("");

    if (!code) {
      setErrorMessage("Please enter your code.");
      setIsLoading(false);
      return;
    }

    try {

      await deleteExpiredNotes();

      const q = query(
        collection(db, "notes"),
        where("code", "==", code)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setErrorMessage("Invalid code or already used.");
        return;
      }

      const docSnap = querySnapshot.docs[0];
      const data = docSnap.data();

      if (data.expiryAt) {
        const expiryTime = data.expiryAt.toMillis();

        if (Date.now() > expiryTime) {

          await deleteDoc(doc(db, "notes", docSnap.id));

          setErrorMessage("This code has expired.");
          return;
        }
      }

      setRetrievedText(data.text);
      updateCounter("retrieve_click");

      if (data.oneTimeView === true) {
        await deleteDoc(doc(db, "notes", docSnap.id));
      }

    } catch (error) {
      console.error("Error retrieving data:", error);
      setErrorMessage("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateCounter("website_load");
  }, []);

  return (
    <div className="max-w-6xl mx-auto overflow-x-hidden px-4 py-4 sm:px-0">
      <div className="text-center mb-6">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-titleLight dark:text-titleDark">
          Share Text Online
        </h1>
        <p className="text-subText dark:text-subTextDark mt-4 text-base sm:text-lg md:text-xl leading-relaxed">
          No login required. Just paste your text, generate a code, and share it online for free.<br></br> The fastest free online clipboard for any device.
        </p>
        <div className="flex justify-center mt-8">
          <div className="flex bg-input dark:bg-inputDark p-2 rounded-2xl shadow-sm gap-2">
            <button
              onClick={() => setActiveTab("share")}
              className={`px-6 py-2 rounded-xl text-sm font-semibold transition flex items-center gap-2 ${activeTab === "share"
                ? "bg-primary text-whiteCustom shadow"
                : "text-textMain dark:text-textDark hover:bg-borderLight dark:hover:bg-borderDark"
                }`}
            >
              <FontAwesomeIcon icon={faUpload} />
              Share Text
            </button>

            <button
              onClick={() => setActiveTab("retrieve")}
              className={`px-6 py-2 rounded-xl text-sm font-semibold transition flex items-center gap-2 ${activeTab === "retrieve"
                ? "bg-primary text-whiteCustom shadow"
                : "text-textMain dark:text-textDark hover:bg-borderLight dark:hover:bg-borderDark"
                }`}
            >
              <FontAwesomeIcon icon={faDownload} />
              Retrieve Text
            </button>
          </div>
        </div>
      </div>

      <div className="bg-card dark:bg-cardDark rounded-[28px] border border border-borderLight dark:border-borderDark max-w-4xl mx-auto overflow-hidden">
        <div
          className={`flex transition-transform duration-500 ease-in-out ${activeTab === "retrieve"
            ? "-translate-x-1/2"
            : "translate-x-0"
            }`}
          style={{ width: "200%" }}
        >
          {/* SHARE SECTION */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10">

            {/* <h2 className="text-[28px] font-semibold text-titleLight dark:text-titleDark mb-8">
              Clipboard super notes
            </h2> */}

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste or type your text here..."
              className="w-full h-56 resize-none rounded-2xl border-2 border-borderLight dark:border-borderDark bg-input dark:bg-inputDark p-6 text-[15px]
               text-textMain dark:text-textDark placeholder-placeholder dark:placeholder-placeholderDark shadow-inner focus:ring-2 focus:ring-borderLight dark:focus:ring-borderLight"
            />

            <div className="flex flex-col md:flex-row gap-6 mt-8 items-end">
              <div className="w-full md:w-1/2">
                <label className="block text-[15px] text-textMain dark:text-textDark mb-3">
                  Expiry Time
                </label>
                <div className="relative">
                  <select
                    value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}
                    className="w-full h-14 appearance-none rounded-2xl bg-input dark:bg-inputDark text-textMain dark:text-textDark px-6 pr-12 text-[16px] focus:outline-none"
                  >
                    <option>1 Minutes</option>
                    <option>30 Minutes</option>
                    <option>1 Hour</option>
                    <option>24 Hours</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-5 flex items-center text-textMain dark:text-textDark">
                    <FontAwesomeIcon icon={faChevronDown} />
                  </div>
                </div>
              </div>

              <div className="w-full md:w-1/2 flex items-center gap-3 bg-input dark:bg-inputDark px-6 h-14 rounded-2xl">
                <input
                  type="checkbox"
                  checked={oneTime}
                  onChange={() => setOneTime(!oneTime)}
                  className="appearance-none h-6 w-6 rounded-lg border-2 border-borderLight dark:border-borderDark bg-boxLight dark:bg-inputDeepDark checked:bg-primary checked:border-primary relative cursor-pointer transition-all duration-200 before:content-['✓'] before:absolute before:text-whiteCustom before:text-sm before:font-bold before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:opacity-0 checked:before:opacity-100"
                />
                <label className="text-[15px] text-textMain dark:text-textDark">
                  One-time view
                </label>
              </div>

              {/* auto generate and auto copy 
               <button
                onClick={() => generateCode(true)}
                type="button"
                className={`h-14 w-14 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg
                   ${quickCopied
                    ? "bg-green-600 scale-110 shadow-[0_0_20px_rgba(34,197,94,0.6)]"
                    : "bg-primary hover:bg-primaryHover"
                  }
                      `}
              >
                <FontAwesomeIcon
                  icon={quickCopied ? faCheck : faClipboard}
                  size="lg"
                  className="text-whiteCustom transition-all duration-300"
                />
              </button> */}
            </div>

            <button
              onClick={generateCode}
              type="button"
              className="w-full mt-10 bg-gradient-to-r from-primary to-primaryHover text-whiteCustom py-5 rounded-full font-semibold text-[16px] hover:opacity-95 transition flex items-center justify-center gap-3 shadow-[0_10px_30px_rgba(37,99,235,0.35)]"
            >
              <FontAwesomeIcon icon={faUpload} />
              Share Text
            </button>

            {generateError && (
              <div className="w-full mt-6 p-4 bg-errorLight border border-error text-error rounded-xl text-center font-medium animate-pulse">
                {generateError}
              </div>
            )}

            {generatedCode && (
              <div ref={resultRef}
                className="mt-8 bg-successLight border border-success
 rounded-2xl p-6 shadow-lg text-left
              dark:bg-gradient-to-br dark:bg-successDark dark:border-success dark:shadow-[0_0_40px_rgba(34,197,94,0.35)]">
                <h3 className="text-success dark:text-success font-semibold mb-4">
                  Your code is ready!
                </h3>

                <div className="flex items-center justify-between bg-boxLight dark:bg-boxDark rounded-xl px-6 py-4">
                  <span className="text-3xl font-bold tracking-widest text-titleLight dark:text-titleDark">
                    {generatedCode}
                  </span>

                  <button
                    onClick={copyCode}
                    className={`h-12 w-12 rounded-xl flex items-center justify-center transition-all duration-300 shadow-md
                         ${copied
                        ? "bg-success scale-110 shadow-[0_0_20px_rgba(34,197,94,0.6)]"
                        : "bg-success hover:opacity-90"
                      }
                         `}
                  >
                    <FontAwesomeIcon
                      icon={copied ? faCheck : faCopy}
                      className="text-whiteCustom transition-all duration-300"
                    />
                  </button>
                </div>

                <p className="text-success dark:text-success mt-4 text-sm">
                  Share this code with anyone to let them access your text.
                </p>
              </div>
            )}
          </div>

          {/* RETRIEVE SECTION */}
          <div className="w-full md:w-1/2 p-6 sm:p-8 md:p-10 flex flex-col items-center justify-start">
            <h2 className="text-[28px] font-semibold text-titleLight dark:text-titleDark mb-8">
              Enter 6-digit Code
            </h2>

            <div className="flex justify-center gap-2 sm:gap-4 md:gap-6 mb-8 w-full max-w-xs sm:max-w-sm md:max-w-md">
              {retrieveCode.map((digit, i) => (
                <input
                  key={i}
                  id={`code-${i}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}

                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedData = e.clipboardData.getData("text").trim();

                    if (!/^\d{6}$/.test(pastedData)) return;

                    const newCode = pastedData.split("");
                    setRetrieveCode(newCode);
                    document.getElementById("code-5")?.focus();
                  }}

                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^[0-9]?$/.test(value)) return;

                    const newCode = [...retrieveCode];
                    newCode[i] = value;
                    setRetrieveCode(newCode);

                    if (value && i < 5) {
                      document.getElementById(`code-${i + 1}`).focus();
                    }
                  }}

                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && !retrieveCode[i] && i > 0) {
                      document.getElementById(`code-${i - 1}`).focus();
                    }
                  }}

                  className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 text-center text-lg sm:text-xl font-semibold rounded-2xl transition-all duration-200
            bg-input text-textMain dark:bg-inputDeepDark dark:text-whiteCustom dark:border dark:border-borderDark dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]
            focus:outline-none focus:ring-2 focus:ring-primary"
                />
              ))}
            </div>

            <div className="flex flex-wrap justify-center items-center gap-4">

              {/* GET DATA BUTTON */}
              <button
                onClick={handleRetrieve}
                disabled={isLoading}
                className={`px-10 py-3 rounded-full font-semibold flex items-center gap-2 transition text-whiteCustom shadow
                    ${isLoading
                    ? "bg-disabled cursor-not-allowed"
                    : "bg-primary hover:bg-primaryHover dark:bg-primary dark:hover:bg-primaryHover"
                  }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-whiteCustom border-t-transparent rounded-full animate-spin"></div>
                    Loading...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faDownload} />
                    Get Data
                  </>
                )}
              </button>

              {/* COPY BUTTON - show only when text retrieved */}
              {/* {retrievedText && (
                <button
                  onClick={handleCopy}
                  className={`h-11 w-11 flex items-center justify-center transition-all duration-300 rounded-xl
                   ${retrievedCopied
                      ? "text-success scale-110"
                      : "text-textMain hover:text-titleLight dark:text-textDark dark:hover:text-titleDark"
                    }
                      `}
                >
                  <FontAwesomeIcon
                    icon={retrievedCopied ? faCheck : faCopy}
                    size="lg"
                    className="transition-all duration-300"
                  />
                </button>
              )} */}

            </div>

            {errorMessage && (
              <div className="w-full mt-6 p-4 bg-errorLight border border-error text-error rounded-xl text-center font-medium animate-pulse">
                {errorMessage}
              </div>
            )}

            {retrievedText && (
              <div className="w-full mt-10">
                <div className="border-t border-borderLight dark:border-borderDark mb-6"></div>

                <div className="w-full mt-10">
                  <div className="border-t border-borderLight dark:border-borderDark mb-6"></div>

                  <div className="relative w-full">

                    {/* TEXTAREA */}
                    <textarea
                      value={retrievedText}
                      readOnly
                      className="w-full h-56 resize-none rounded-2xl border-2 border-borderLight 
  dark:border-borderDark bg-input dark:bg-inputDark p-6 pr-20 text-[15px]
  text-textMain dark:text-textDark shadow-inner overflow-y-auto"
                    />

                    {/* COPY BUTTON INSIDE */}
                    <button
                      onClick={handleCopy}
                      className={`absolute top-4 right-4 h-11 w-11 flex items-center justify-center transition-all duration-300 rounded-xl
  ${retrievedCopied
                          ? "text-success scale-110"
                          : "text-textMain hover:text-titleLight dark:text-textDark dark:hover:text-titleDark"
                        }`}
                    >
                      <FontAwesomeIcon
                        icon={retrievedCopied ? faCheck : faCopy}
                        size="lg"
                        className="transition-all duration-300"
                      />
                    </button>

                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* WHAT IS SECTION */}
      <div className="mt-10 max-w-4xl mx-auto bg-card dark:bg-cardDark rounded-[28px] border border-borderLight dark:border-borderDark p-10">

        <h2 className="text-3xl font-semibold text-titleLight dark:text-titleDark mb-6">
          What is ClickBoard?
        </h2>

        <p className="text-textMain dark:text-textDark leading-8 mb-6">
          ClickBoard is a fast, secure, and easy-to-use online clipboard tool that lets you share text instantly between devices no login or complicated setup needed. Whether you’re a developer, student, office worker, or just someone who frequently switches between devices, ClickBoard offers a simple way to transfer notes, links, passwords, or code snippets in seconds.
        </p>

        <p className="text-textMain dark:text-textDark leading-8 mb-6">
          In today’s world, people often move between phones, laptops, and desktops. Traditional ways to share text, like messaging apps or email, often require accounts, syncing, or multiple steps. ClickBoard removes these hassles by working right in your browser with a clean and minimal interface.
        </p>

        <p className="text-textMain dark:text-textDark leading-8 mb-6">
          Using ClickBoard is straightforward: paste your text, create a unique 6-digit code, then use that code on any other device to instantly access the text. It’s perfect for quick sharing without wasting time. Plus, your privacy is protected because data isn’t stored permanently and is automatically deleted after your chosen expiry time.
        </p>

        <p className="text-textMain dark:text-textDark leading-8 mb-8">
          ClickBoard also includes a one-time view feature, so your text can only be seen once before it’s deleted ideal for sharing sensitive info securely. The entire system is lightweight, fast, and works smoothly across all devices.
        </p>

        {/* FEATURES */}
        <h3 className="text-2xl font-semibold text-titleLight dark:text-titleDark mb-4">
          Key Features
        </h3>

        <ul className="list-disc ml-6 text-textMain dark:text-textDark mb-8 space-y-2">
          <li>Instant text sharing with a secure 6-digit code</li>
          <li>No login, sign-up, or account needed</li>
          <li>Works on mobile, tablet, and desktop</li>
          <li>Customizable expiry times (1 minute to 24 hours)</li>
          <li>One-time view option for sensitive data</li>
          <li>Automatic deletion of expired data for privacy</li>
          <li>Clean, modern, and easy-to-use design</li>
          <li>Fast, real-time text retrieval</li>
        </ul>

        {/* COMMON USES */}
        <h3 className="text-2xl font-semibold text-titleLight dark:text-titleDark mb-4">
          Common Uses
        </h3>

        <ul className="list-disc ml-6 text-textMain dark:text-textDark mb-8 space-y-2">
          <li>Quickly transfer text between your phone and laptop</li>
          <li>Share code snippets among developers</li>
          <li>Send links, notes, or temporary info without emails or apps</li>
          <li>Move content between work and personal devices securely</li>
          <li>Share sensitive data with one-time access codes</li>
          <li>Use as a temporary cloud clipboard for quick productivity</li>
        </ul>

        {/* BENEFITS */}
        <h3 className="text-2xl font-semibold text-titleLight dark:text-titleDark mb-4">
          Benefits
        </h3>

        <ul className="list-disc ml-6 text-textMain dark:text-textDark mb-8 space-y-2">
          <li>Saves time versus traditional sharing methods</li>
          <li>No apps to install or accounts to create</li>
          <li>Instant and simple to use</li>
          <li>Enhanced privacy with automatic expiry and deletion</li>
          <li>Lightweight and accessible from any browser</li>
          <li>Reduces reliance on third-party apps</li>
        </ul>

        {/* HOW IT WORKS */}
        <h3 className="text-2xl font-semibold text-titleLight dark:text-titleDark mb-4">
          How It Works
        </h3>

        <ol className="list-decimal ml-6 text-textMain dark:text-textDark space-y-2 mb-6">
          <li>Paste or type your text into the input box</li>
          <li>Choose an expiry time and enable one-time view if needed</li>
          <li>Click “Share Text” to get a unique 6-digit code</li>
          <li>On another device, open ClickBoard and enter the code</li>
          <li>Click “Get Data” to instantly retrieve your text</li>
        </ol>

        <p className="text-textMain dark:text-textDark leading-8">
          This quick, secure process removes the need for complex file transfers or messaging apps. With its focus on speed, privacy, and simplicity, ClickBoard is a perfect tool for anyone who needs to share text quickly and safely across devices.
        </p>

      </div>

      <Footer />

    </div >
  );
}
